import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import "./homepage.css";
import { ThemeContext } from "../App";
import Recipe from "../components/recepie-item/Recipe";
import { useOutletContext, useSearchParams } from "react-router-dom";
import Modal from "../components/Modal";
import Select from "../components/select/Select";
import IngredientSearch from "../components/ingredient/IngredientSearch";
import { EmptyState } from "../components/common/Common";
import { SearchX, CloudOff, History, X } from "lucide-react";
import {
  SPOONACULAR_RANDOM_RECIPE_API,
  SPOONACULAR_RECIPE_API,
  THEMEALDB_SEARCH_API,
  THEMEALDB_FILTER_BY_CATEGORY_API,
  THEMEALDB_CATEGORIES_API,
  THEMEALDB_AREAS_API,
  THEMEALDB_FILTER_BY_AREA_API
} from "../config";
import { fetchByIngredients, fetchRecipeDetails, spoonFetchJSON, toMealItem } from "../api/recipes";
import { useRecentlyViewed } from "../utils/stores";
import Swal from "sweetalert2";

const excludedWords = ["beef"];
const PAGE_SIZE = 12;

const Homepage = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [api, setApi] = useState("themealdb"); // 'spoonacular' or 'themealdb'
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const sentinelRef = useRef(null);

  const { theme } = useContext(ThemeContext);
  const { search } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const { recent, clear: clearRecent } = useRecentlyViewed();

  // Centralized data fetching logic
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      setRecipes([]); // Clear old recipes
      try {
        let data = [];
        const mealList = (result) => (result.meals || []).map(toMealItem);

        if (searchQuery) {
          // 1. Handle Search
          if (api === "spoonacular") {
            const base = SPOONACULAR_RECIPE_API;
            const result = await spoonFetchJSON(
              `${base}${base.includes("?") ? "&" : "?"}query=${encodeURIComponent(searchQuery)}`
            );
            data = (result.results || []).map((r) => ({ ...r, isMealDB: false }));
          } else {
            const res = await fetch(`${THEMEALDB_SEARCH_API}?s=${encodeURIComponent(searchQuery)}`);
            data = mealList(await res.json());
          }
        } else if (api === "themealdb" && ingredients.length) {
          // 2. Find by ingredient(s) (TheMealDB only)
          data = await fetchByIngredients(ingredients);
        } else if (api === "themealdb" && selectedCategory) {
          // 3. Handle Category Filter (TheMealDB only)
          const res = await fetch(`${THEMEALDB_FILTER_BY_CATEGORY_API}?c=${selectedCategory}`);
          data = mealList(await res.json());
        } else if (api === "themealdb" && selectedArea) {
          // 4. Handle Area Filter (TheMealDB only)
          const res = await fetch(`${THEMEALDB_FILTER_BY_AREA_API}?a=${selectedArea}`);
          data = mealList(await res.json());
        } else if (api === "spoonacular") {
          // 5. Default: random recipes
          const result = await spoonFetchJSON(SPOONACULAR_RANDOM_RECIPE_API);
          data = (result.recipes || []).map((r) => ({ ...r, isMealDB: false }));
        } else {
          const res = await fetch(`${THEMEALDB_FILTER_BY_AREA_API}?a=British`);
          data = mealList(await res.json());
        }
        if (!cancelled) {
          setRecipes(data);
          setVisibleCount(PAGE_SIZE);
        }
      } catch (err) {
        console.error("Failed to fetch recipes:", err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, searchQuery, selectedCategory, selectedArea, ingredients, reloadKey]);

  // Fetch categories and areas for TheMealDB
  useEffect(() => {
    if (api === "themealdb") {
      fetch(THEMEALDB_CATEGORIES_API)
        .then((res) => res.json())
        .then((data) => {
          const filteredCategories = (data.categories || []).filter(
            (cat) =>
              !excludedWords.some((word) =>
                cat.strCategory.toLowerCase().includes(word.toLowerCase())
              )
          );
          setCategories(filteredCategories);
        })
        .catch(() => {});

      fetch(THEMEALDB_AREAS_API)
        .then((res) => res.json())
        .then((data) => setAreas(data.meals || []))
        .catch(() => {});
    } else {
      setCategories([]);
      setAreas([]);
    }
  }, [api]);

  // Search submitted from the global navbar
  useEffect(() => {
    if (search.query) {
      setSelectedCategory("");
      setSelectedArea("");
      setIngredients([]);
      setSearchQuery(search.query);
    }
  }, [search]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setSelectedArea("");
    setIngredients([]);
  };

  // Handlers just update state, triggering the main useEffect
  const handleCategoryFilter = (category) => {
    setSearchQuery(""); // Clear search when filtering
    setSelectedArea(""); // Clear area when filtering
    setIngredients([]);
    setSelectedCategory(category);
  };

  const handleAreaFilter = (area) => {
    setSearchQuery(""); // Clear search when filtering
    setSelectedCategory(""); // Clear category when filtering
    setIngredients([]);
    setSelectedArea(area);
  };

  const handleIngredients = (list) => {
    setSearchQuery("");
    setSelectedCategory("");
    setSelectedArea("");
    setIngredients(list);
  };

  const handleApiChange = (newApi) => {
    clearFilters();
    setApi(newApi);
  };

  const handleViewRecipe = useCallback(async (item) => {
    try {
      const data = await fetchRecipeDetails(item);
      setSelectedRecipe(data);
      setModalOpen(true);
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "Failed to load recipe details.", "error");
    }
  }, []);

  // Open a recipe from a shared link: /?recipe=ID&source=themealdb|spoonacular
  useEffect(() => {
    const id = params.get("recipe");
    if (!id) return;
    handleViewRecipe({ id, isMealDB: params.get("source") !== "spoonacular" });
    setParams({}, { replace: true });
  }, [params, setParams, handleViewRecipe]);

  const visibleRecipes = recipes.filter(
    (item) =>
      !excludedWords.some((word) =>
        item.title.toLowerCase().includes(word.toLowerCase())
      )
  );
  const shown = visibleRecipes.slice(0, visibleCount);
  const hasMore = visibleCount < visibleRecipes.length;

  // Infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => entries[0].isIntersecting && setVisibleCount((c) => c + PAGE_SIZE),
      { rootMargin: "400px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, visibleCount]);

  const hasActiveFilter = !!(searchQuery || selectedCategory || selectedArea || ingredients.length);
  const emptyTitle = searchQuery
    ? `No recipes found for “${searchQuery}”`
    : ingredients.length
    ? "No recipe uses all of those"
    : "Nothing here yet";

  return (
    <>
      <main className={`home-container ${theme ? "dark" : ""}`}>
        <div className="hero-section">
          <span className="hero-eyebrow">Fresh from every kitchen</span>
          <h1 className="fade-in">
            Discover, Cook <em>&amp; Enjoy</em> 🍳
          </h1>
          <p className="fade-in-delay">
            Explore random recipes or search your favorite dishes.
          </p>
        </div>

        <div className="api-controls">
          <div className="api-toggle" role="group" aria-label="Recipe source">
            <button
              className={`api-btn ${api === "themealdb" ? "active" : ""}`}
              onClick={() => handleApiChange("themealdb")}
              aria-pressed={api === "themealdb"}
            >
              TheMealDB
            </button>
            <button
              className={`api-btn ${api === "spoonacular" ? "active" : ""}`}
              onClick={() => handleApiChange("spoonacular")}
              aria-pressed={api === "spoonacular"}
            >
              Spoonacular
            </button>
          </div>

          {api === "themealdb" && (
            <div className="filters">
              <Select
                className="category-filter"
                ariaLabel="Filter by category"
                value={selectedCategory}
                onChange={handleCategoryFilter}
                options={[
                  { value: "", label: "All Categories" },
                  ...categories.map((cat) => ({
                    value: cat.strCategory,
                    label: cat.strCategory,
                  })),
                ]}
              />

              <Select
                className="area-filter"
                ariaLabel="Filter by area"
                value={selectedArea}
                onChange={handleAreaFilter}
                options={[
                  { value: "", label: "All Areas" },
                  ...areas.map((area) => ({
                    value: area.strArea,
                    label: area.strArea,
                  })),
                ]}
              />
            </div>
          )}
        </div>

        {api === "themealdb" && (
          <IngredientSearch selected={ingredients} onChange={handleIngredients} />
        )}

        {recent.length > 0 && !hasActiveFilter && (
          <section className="recent" aria-label="Recently viewed">
            <div className="recent-head">
              <h2><History size={18} aria-hidden="true" /> Recently viewed</h2>
              <button className="recent-clear" onClick={clearRecent}>
                <X size={14} /> Clear
              </button>
            </div>
            <div className="recent-row">
              {recent.map((r) => (
                <button key={r.id} className="recent-card" onClick={() => handleViewRecipe(r)}>
                  <img src={r.image} alt="" loading="lazy" />
                  <span>{r.title}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {loading && (
          <>
            <div className="loading" role="status">
              Loading Recipes...
            </div>
            <div className="items" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <div className="skeleton-card" key={i}>
                  <div className="skeleton-img" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line short" />
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && error && (
          <EmptyState
            icon={CloudOff}
            title="Couldn't load recipes"
            text="Check your connection and try again."
            action={{ label: "Try again", onClick: () => setReloadKey((k) => k + 1) }}
          />
        )}

        {!loading && !error && visibleRecipes.length === 0 && (
          <EmptyState
            icon={SearchX}
            title={emptyTitle}
            text="Try another keyword or loosen your filters."
            action={hasActiveFilter ? { label: "Clear search & filters", onClick: clearFilters } : null}
          />
        )}

        <div className="items">
          {shown.map((item, index) => (
            <div
              className="fade-up"
              key={item.id}
              style={{ animationDelay: `${Math.min(index % PAGE_SIZE, 12) * 45}ms` }}
            >
              <Recipe
                item={item}
                id={item.id}
                image={item.image}
                title={item.title}
                onViewRecipe={() => handleViewRecipe(item)}
              />
            </div>
          ))}
        </div>

        {hasMore && (
          <div className="load-more" ref={sentinelRef}>
            <button className="load-more-btn" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
              Show more recipes ({visibleRecipes.length - visibleCount} left)
            </button>
          </div>
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          recipe={selectedRecipe}
        />
      </main>
    </>
  );
};

export default Homepage;
